/**
 * CAD File Upload Constants
 */

/**
 * Supported 3D CAD file formats for upload
 * Synced with EXCHANGE_INPUT_FORMATS from batch_apcs_hoops_converter/src/job_types.py
 */
export const SUPPORTED_3D_CAD_FORMATS = [
  // General formats
  '.3MF', '.SAT', '.SAB', '.DWG', '.DXF', '.3DS', '.DWF', '.DWFX',
  // Autodesk Inventor / Navisworks
  '.IPT', '.IAM', '.NWD',
  // CATIA V4 / DLV / EXP
  '.MODEL', '.SESSION', '.DLV', '.EXP',
  // CATIA V5/V6
  '.CATDRAWING', '.CATPART', '.CATPRODUCT', '.CATSHAPE', '.CGR', '.3DXML',
  // Collada / Creo / NX / Parasolid
  '.DAE', '.ASM', '.NEU', '.PRT', '.XAS', '.XPR', '.DGN', '.FBX',
  // glTF / Misc
  '.GLTF', '.GLB', '.MF1', '.ARC', '.UNV', '.PKG',
  // IFC (BIM)
  '.IFC', '.IFCZIP',
  // IGES / JT / Parasolid
  '.IGS', '.IGES', '.JT', '.X_T', '.X_B', '.XMT', '.XMT_TXT',
  // PDF (3D PDF)
  '.PDF',
  // PRC / Revit / Rhino
  '.PRC', '.RVT', '.RFA', '.3DM',
  // Solid Edge
  '.PAR', '.PWD', '.PSM',
  // SolidWorks
  '.SLDASM', '.SLDPRT',
  // STEP
  '.STP', '.STEP', '.STPZ', '.STPX', '.STPXZ',
  // STL / U3D / VDA / VRML / OBJ
  '.STL', '.U3D', '.VDA', '.WRL', '.VRML', '.OBJ',
] as const

/**
 * Maximum file size in MB
 */
export const MAX_FILE_SIZE_MB = 50

/**
 * Maximum file size in bytes
 */
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024

/**
 * WebSocket reconnection settings
 */
export const WS_MAX_RECONNECT_ATTEMPTS = 4
export const WS_RECONNECT_DELAY_MS = 3500

/**
 * Polling settings (fallback when WebSocket fails)
 */
export const POLL_INTERVAL_MS = 1000
export const POLL_VIEWER_TIMEOUT_MS = 360 * 1000 // 6 minutes
export const POLL_ANALYSIS_TIMEOUT_MS = 720 * 1000 // 12 minutes

/**
 * Get API base URL based on current hostname
 */
export function getAPIBaseURL(): string {
  const envApiUrl = process.env.REACT_APP_API_URL
  if (envApiUrl) {
    return envApiUrl.endsWith('/') ? envApiUrl : `${envApiUrl}/`
  }

  const hostname = window.location.hostname

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8000/'
  }

  if (hostname === 'dev.assemblean.at') {
    return 'https://api.atlas-staging.assemblean.com/'
  }

  if (hostname.includes('assemblean.com')) {
    return 'https://api.atlas-staging.assemblean.com/'
  }

  return 'https://api.atlas.assemblean.com/'
}

/**
 * Get WebSocket base URL based on current hostname
 * Note: The URL includes the /ws/ prefix as required by the backend routing
 */
export function getWebSocketBaseURL(): string {
  const envWsUrl = process.env.REACT_APP_WS_URL
  if (envWsUrl) {
    // If REACT_APP_WS_URL is set, ensure it has /ws/ prefix and trailing slash
    let url = envWsUrl.endsWith('/') ? envWsUrl : `${envWsUrl}/`
    // Check if /ws/ is already in the URL
    if (!url.includes('/ws/')) {
      // Add /ws/ before the trailing slash
      url = url.replace(/\/$/, '/ws/')
    }
    return url
  }

  const hostname = window.location.hostname

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'ws://localhost:8000/ws/'
  }

  if (hostname === 'dev.assemblean.at') {
    return 'wss://api.atlas-staging.assemblean.com/ws/'
  }

  if (hostname.includes('assemblean.com')) {
    return 'wss://api.atlas.assemblean.com/ws/'
  }

  return 'wss://api.atlas.assemblean.com/ws/'
}

/**
 * Get default guest request page URL
 */
export function getGuestRequestPageURL(): string {
  const baseUrl = window.location.origin
  return `${baseUrl}/guestRequest`
}

/**
 * Default quantity for uploads
 */
export const DEFAULT_QUANTITY = 1

